import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'wouter';
import {
  ChevronRight,
  ArrowLeft,
  Filter,
  Calendar,
  FileText,
  Plane,
  BarChart3,
  Globe,
  LayoutGrid,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { isAdminRole, isSystemAdminRole } from '@/lib/roles';
import { isFeatureEnabled } from '@/config/featureFlags';
import { useAlphaFeatureGates } from '@/hooks/useAlphaFeatureGates';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import {
  activeAdminSection,
  adminSectionDefaultPath,
  visibleAdminSections,
  type AdminNavSection,
} from '@/lib/adminNav';
import { UserMenu } from './UserMenu';
import { Phase1Rail } from './Phase1Rail';
import { PHASE1_FLAGS } from '@/lib/phase1Paths';
import { BELOW_CHROME_HEIGHT } from '@/components/GlobalHeader';
import { usePrimaryNavItems } from '@/hooks/usePrimaryNav';
import { useMyAdminModuleAccess } from '@/lib/adminModuleAccess';

type CanvasType =
  | 'threads'
  | 'pipeline'
  | 'clients'
  | 'trips'
  | 'proposals'
  | 'booking'
  | 'payments'
  | 'analytics'
  | 'content'
  | 'dashboard';

interface NavItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path?: string;
  /** Additional path prefixes that also activate this item (for parent nav entries). */
  matchPaths?: string[];
  canvas?: CanvasType;
  disabled?: boolean;
  comingSoon?: boolean;
  advancedOnly?: boolean;
  /** If true, only visible to SYSTEM_ADMIN (not basic ADMIN) */
  systemAdminOnly?: boolean;
  description?: string;
  /** Admin section this entry represents (see lib/adminNav). */
  sectionId?: string;
}


interface UnifiedSidebarProps {
  /** Callback when navigating to a canvas (main mode) */
  onNavigate?: (canvas: CanvasType) => void;
  /** Callback when mouse enters the threads nav item */
  onThreadsMouseEnter?: () => void;
  /** Callback when mouse leaves the threads nav item */
  onThreadsMouseLeave?: () => void;
  /** Whether to hide the clients nav item */
  hideClients?: boolean;
  /**
   * AR-316: render for the mobile nav drawer — always expanded, full width of
   * the drawer, no collapse control, and taller rows for touch.
   */
  inDrawer?: boolean;
  /** Called after a nav row navigates, so the drawer can close itself. */
  onNavigated?: () => void;
  /** Set false when the host (the mobile drawer) renders the user menu itself. */
  showUserMenu?: boolean;
}

/**
 * UnifiedSidebar handles both main app and admin modes in a single component.
 * - Detects if we're on an admin route and shows admin navigation
 * - Shows main app navigation on non-admin routes
 * - Includes "Return to Site" link in admin mode
 * - Uses shared UserMenu component for consistent user menu behavior
 *
 * This is the unified sidebar for T-385 (Admin Sidebar Consolidation).
 */
export const UnifiedSidebar: React.FC<UnifiedSidebarProps> = ({
  onNavigate,
  onThreadsMouseEnter,
  onThreadsMouseLeave,
  hideClients = false,
  inDrawer = false,
  onNavigated,
  showUserMenu = true,
}) => {
  const [location, navigate] = useLocation();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(() => {
    // Default to expanded on admin routes, collapsed on main routes
    return location.startsWith('/admin');
  });

  // Track which disabled item was clicked to show inline "Coming soon" message
  const [clickedDisabledItem, setClickedDisabledItem] = useState<string | null>(null);

  // Auto-hide the "Coming soon" message after 2 seconds
  useEffect(() => {
    if (clickedDisabledItem) {
      const timer = setTimeout(() => setClickedDisabledItem(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [clickedDisabledItem]);

  // Detect if we're in admin mode
  const isAdminRoute = location.startsWith('/admin');
  const isAdmin = isAdminRole(user?.role);
  const showAdminMode = isAdminRoute && isAdmin;

  // Feature flags for conditional nav items
  const advancedFlagEnabled = isFeatureEnabled('agenticAdminAdvanced');
  const isSystemAdmin = isSystemAdminRole(user?.role);

  // Alpha feature gates for non-admin users
  const { isEnabled: isResolvedEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();

  /* AR-316 — the primary destinations now come from usePrimaryNavItems() so the
     desktop rail and the mobile tab bar cannot drift apart. `canvas` is added
     back here because only the rail uses it. */
  const sharedNavItems = usePrimaryNavItems();
  const mainNavItems: NavItem[] = useMemo(
    () =>
      sharedNavItems.map((item) =>
        item.path === '/threads' ? { ...item, canvas: 'threads' as const } : item,
      ),
    [sharedNavItems],
  );


  // Admin sections the current user may see (AR-285). Finances-gated pages
  // (AR-251) additionally require the resolved invoicingMvp flag. Role &
  // Access panel grants (per-user admin-module overrides) unlock
  // systemAdminOnly pages for Admins.
  const financesEnabled = !flagsLoading && isResolvedEnabled('invoicingMvp');
  const onboardingEnabled = !flagsLoading && isResolvedEnabled('onboarding');
  const { moduleAccess } = useMyAdminModuleAccess();
  const adminSections: AdminNavSection[] = useMemo(
    () =>
      visibleAdminSections({
        isSystemAdmin,
        advancedFlagEnabled,
        financesEnabled,
        onboardingEnabled,
        moduleAccess,
      }),
    [isSystemAdmin, advancedFlagEnabled, financesEnabled, onboardingEnabled, moduleAccess],
  );

  // Primary admin nav = one entry per section, pointing at its default sub-page
  const adminNavItems: NavItem[] = useMemo(
    () =>
      adminSections.map((section) => ({
        icon: section.icon,
        label: section.label,
        path: adminSectionDefaultPath(section),
        sectionId: section.id,
      })),
    [adminSections],
  );

  const activeSectionId = showAdminMode
    ? activeAdminSection(location, adminSections)?.id
    : undefined;

  const navItems = showAdminMode ? adminNavItems : mainNavItems;

  const isNavItemActive = (item: NavItem): boolean => {
    if (item.sectionId) {
      return item.sectionId === activeSectionId;
    }
    if (item.matchPaths) {
      return item.matchPaths.some(
        (p) => location === p || location.startsWith(p + '/'),
      );
    }
    if (item.path) {
      return (
        location === item.path ||
        (!showAdminMode && location.startsWith(item.path + '/'))
      );
    }
    return false;
  };

  // Calculate sidebar height — inside the mobile drawer the Sheet owns it.
  const sidebarHeight = inDrawer
    ? undefined
    : BELOW_CHROME_HEIGHT;

  const handleNavigateMain = (canvas: CanvasType) => {
    if (onNavigate) {
      onNavigate(canvas);
    } else {
      // Default navigation behavior
      switch (canvas) {
        case 'threads':
          navigate('/threads');
          break;
        case 'clients':
          navigate('/clients');
          break;
        case 'trips':
          navigate('/trips');
          break;
        case 'pipeline':
          navigate('/sales-pipeline');
          break;
        case 'proposals':
          navigate('/proposals');
          break;
        case 'dashboard':
          navigate('/');
          break;
        default:
          navigate('/');
      }
    }
  };

  const handleReturnToSite = () => {
    navigate('/threads');
  };

  const handleToggleExpanded = () => setExpanded(!expanded);

  // AR-445 — with the Phase-1 flags on, the member side of the app runs on the
  // Phase-1 navigation, in every layout that shows this rail. Admin keeps its own.
  const phase1On = !flagsLoading && PHASE1_FLAGS.every((f) => isResolvedEnabled(f));
  if (phase1On && !showAdminMode) {
    return <Phase1Rail inDrawer={inDrawer} onNavigated={onNavigated} showUserMenu={showUserMenu} />;
  }

  if (!expanded && !inDrawer) {
    // Collapsed sidebar
    return (
      <div
        data-app-chrome
        className="relative flex w-12 flex-col bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700"
        style={{ height: sidebarHeight }}
      >
        {/* Expand button */}
        <button
          onClick={handleToggleExpanded}
          className="absolute top-1/2 right-[-15px] z-[200] flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-md transition-all hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
          title="Expand sidebar"
          aria-label="Expand sidebar"
        >
          <ChevronRight className="w-3 h-3 text-gray-400 dark:text-gray-300" />
        </button>

        {/* Return to Site (admin mode only) */}
        {showAdminMode && (
          <button
            onClick={handleReturnToSite}
            className="group relative flex h-8 w-8 mx-2 mt-3 items-center justify-center rounded-lg text-gray-600 dark:text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-orange-600 dark:hover:text-orange-400"
            title="Return to Site"
          >
            <ArrowLeft className="h-4 w-4" />
            <div className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded bg-gray-900 dark:bg-gray-700 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 z-50">
              Return to Site
            </div>
          </button>
        )}

        {/* Navigation */}
        <nav
          aria-label={showAdminMode ? 'Admin sections' : 'Main'}
          className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-2"
        >
          {navItems.map((item) => {
            const isActive = isNavItemActive(item);
            const Icon = item.icon;

            return (
              <button
                type="button"
                key={item.label}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={item.disabled || undefined}
                onClick={() => {
                  if (item.disabled && item.comingSoon) {
                    setClickedDisabledItem(item.label);
                    return;
                  }
                  if (item.disabled) return;
                  if (item.path) {
                    navigate(item.path);
                  } else if (item.canvas) {
                    handleNavigateMain(item.canvas);
                  }
                }}
                onMouseEnter={() =>
                  item.canvas === 'threads' ? onThreadsMouseEnter?.() : undefined
                }
                onMouseLeave={() =>
                  item.canvas === 'threads' ? onThreadsMouseLeave?.() : undefined
                }
                className={cn(
                  "group relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors before:absolute before:-inset-x-2 before:-inset-y-1 before:content-['']",
                  isActive
                    ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                  item.disabled && 'cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent',
                )}
                title={item.label}
              >
                <Icon className={cn('h-4 w-4', item.disabled && 'text-gray-300 dark:text-gray-600')} />
                {/* Hover tooltip */}
                <div className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded bg-gray-900 dark:bg-gray-700 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 z-50">
                  {item.label}
                  {item.comingSoon && ' (Coming soon)'}
                </div>
                {/* Inline "Coming soon" notification on click */}
                {clickedDisabledItem === item.label && (
                  <div className="absolute left-full ml-2 whitespace-nowrap rounded-md bg-amber-100 dark:bg-amber-900/80 border border-amber-300 dark:border-amber-700 px-2 py-1 text-xs font-medium text-amber-800 dark:text-amber-200 z-50 animate-in fade-in slide-in-from-left-1 duration-200">
                    Coming soon...
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Menu */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-2">
          <UserMenu expanded={false} />
        </div>
      </div>
    );
  }

  // Expanded sidebar
  return (
    <div
      data-app-chrome
      className={cn(
        'relative flex flex-col bg-gray-50/50 dark:bg-gray-900/50',
        inDrawer
          ? 'w-full border-b border-gray-200 dark:border-gray-700'
          : 'w-56 border-r border-gray-200 dark:border-gray-700',
      )}
      style={{ height: sidebarHeight }}
    >
      {/* Collapse button — desktop rail only */}
      {!inDrawer && (
        <button
          onClick={handleToggleExpanded}
          className="absolute top-4 right-4 z-[200] rounded p-1 text-gray-400 dark:text-gray-500 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
          title="Collapse sidebar"
          aria-label="Collapse sidebar"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
      )}

      {/* Return to Site (admin mode only) */}
      {showAdminMode && (
        <div className="px-4 pt-4">
          <button
            onClick={handleReturnToSite}
            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Return to Site</span>
          </button>
        </div>
      )}

      {/* Navigation */}
      <div className={cn('px-3 pt-4 pb-2', !inDrawer && 'flex-1 overflow-y-auto')}>
        {showAdminMode ? (
          <div className="mb-3">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-tight">Admin Center</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Control access, configure modules, and manage settings.
            </p>
          </div>
        ) : (
          <p className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-tight">Navigation</p>
        )}
        {/* Admin sections and main app destinations use the same rows (AR-285). */}
        <nav aria-label={showAdminMode ? 'Admin sections' : 'Main'} className="space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = isNavItemActive(item);
            return (
              <button
                key={item.label}
                aria-disabled={item.disabled || undefined}
                onClick={() => {
                  if (item.disabled && item.comingSoon) {
                    setClickedDisabledItem(item.label);
                    return;
                  }
                  if (item.disabled) return;
                  if (item.path) {
                    navigate(item.path);
                  } else if (item.canvas) {
                    handleNavigateMain(item.canvas);
                  }
                  onNavigated?.();
                }}
                onMouseEnter={() =>
                  item.canvas === 'threads' ? onThreadsMouseEnter?.() : undefined
                }
                onMouseLeave={() =>
                  item.canvas === 'threads' ? onThreadsMouseLeave?.() : undefined
                }
                className={cn(
                  'relative flex w-full items-center space-x-3 rounded-lg px-3 text-sm text-left transition-colors',
                  inDrawer ? 'min-h-11 py-2.5' : 'py-2',
                  item.disabled
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent'
                    : isActive
                      ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className={cn('w-4 h-4', item.disabled && 'text-gray-300 dark:text-gray-600')} />
                <span className={item.disabled ? 'text-gray-300 dark:text-gray-600' : ''}>{item.label}</span>
                {item.comingSoon && (
                  <span className="ml-auto text-xs text-gray-300 dark:text-gray-600">Soon</span>
                )}
                {/* Inline "Coming soon" notification on click */}
                {clickedDisabledItem === item.label && (
                  <div className="absolute left-full ml-2 whitespace-nowrap rounded-md bg-amber-100 dark:bg-amber-900/80 border border-amber-300 dark:border-amber-700 px-2 py-1 text-xs font-medium text-amber-800 dark:text-amber-200 z-50 animate-in fade-in slide-in-from-left-1 duration-200">
                    Coming soon...
                  </div>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* User Menu */}
      {showUserMenu && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-3">
          <UserMenu expanded={true} />
        </div>
      )}
    </div>
  );
};

export default UnifiedSidebar;
